import { Link } from "react-router-dom";
import { CloudOff, FileQuestion, LockKeyhole, ShieldX } from "lucide-react";

import { Button, buttonVariants } from "../../ui/button";
import { cn } from "../../../lib/utils";
import { clearAuthAndRedirect } from "../../../hooks/useCheckTokenAndFetchUser";
import {
  OPTRO_ACCESS_RETRY_ACTION,
  OPTRO_ACCESS_SIGN_IN_ACTION,
  optroAccessCopy,
  optroAccessListLink,
} from "../../../lib/optroAccessMessages";
import {
  OptroAccessFailure,
  OptroAccessKind,
} from "../../../models/audit/optroAccess";

// `data-state` values are a test contract — keep them stable.
const DATA_STATE: Record<OptroAccessFailure, string> = {
  signInRequired: "sign-in-required",
  denied: "denied",
  unavailable: "unavailable",
  notFound: "not-found",
};

const ICON: Record<OptroAccessFailure, typeof LockKeyhole> = {
  signInRequired: LockKeyhole,
  denied: ShieldX,
  unavailable: CloudOff,
  notFound: FileQuestion,
};

interface OptroAccessErrorPageProps {
  kind: OptroAccessKind;
  failure: OptroAccessFailure;
  onRetry: () => void;
  // Inside a list page's side panel: no full-height centering, no link back
  // to the list the user is already on.
  compact?: boolean;
}

export const OptroAccessErrorPage = ({
  kind,
  failure,
  onRetry,
  compact = false,
}: OptroAccessErrorPageProps) => {
  const { title, description } = optroAccessCopy(kind, failure);
  const list = optroAccessListLink(kind);
  const Icon = ICON[failure];

  return (
    <div
      data-testid="optro-access-error"
      data-state={DATA_STATE[failure]}
      className={cn(
        "flex flex-col items-center gap-3 px-6 text-center",
        compact ? "py-12" : "h-full min-h-screen justify-center",
      )}
    >
      <Icon className="h-8 w-8 text-stone-400" aria-hidden />
      <h1 className="max-w-md text-base font-medium text-stone-900">{title}</h1>
      <p className="max-w-md text-sm text-stone-500">{description}</p>
      <div className="mt-2 flex items-center gap-2">
        {failure === "signInRequired" && (
          <Button onClick={clearAuthAndRedirect}>
            {OPTRO_ACCESS_SIGN_IN_ACTION}
          </Button>
        )}
        {failure === "unavailable" && (
          <Button onClick={onRetry}>{OPTRO_ACCESS_RETRY_ACTION}</Button>
        )}
        {!compact && (
          // A styled Link rather than <Button asChild>: this project's Button
          // renders a loading slot beside its children, which Slot rejects.
          <Link
            to={list.path}
            className={buttonVariants({ variant: "outline" })}
          >
            {list.label}
          </Link>
        )}
      </div>
    </div>
  );
};
